import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { Option } from '@lib/interfaces/form';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import Trans from '@lib/components/Trans/Trans';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import InterventionAssigneesModal from 'components/InterventionAssigneesModal/InterventionAssigneesModal';
import DepartmentsModalTableMultiselect from 'components/DepartmentsModalTableMultiselect/DepartmentsModalTableMultiselect';
import AssetsModalTableMultiselect from 'components/AssetsModalTableMultiselect/AssetsModalTableMultiselect';
import { FieldNames as AssetFilterFieldNames } from 'components/AssetsModalTableMultiselect/utils/getFilterFields';
import Modal from '@lib/components/Modal/Modal';
import CategoryForm from 'components/InterventionCategory/CategoryForm';
import { FormRef } from '@lib/components/ReactHookForm/types';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  Asset,
  FilterGroupingEnum,
} from 'graphql-common';
import { IconName } from '@lib/components/Modal/enums';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import FieldNames from '@lib/enums/fieldNames/interventionFieldNames';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import TaskAndCompletionsModal from '@lib/components/TaskCompletions/TaskAndCompletionsModal/TaskAndCompletionsModal';
import useTaskAndCompletionsPicker from '@lib/components/TaskCompletions/TaskAndCompletionsModal/useTaskAndCompletionsPicker';
import useTasksTableMultiselectFilterLoadOptions from 'utils/fetch/hooks/useTasksTableMultiselectFilterLoadOptions';
import { taskCompletionsModalUrls } from 'components/TaskCompletionsModal/TaskCompletionsModal';
import InterventionFormPage from './InterventionFormPage/InterventionFormPage';
import InterventionViewPage from './InterventionViewPage/InterventionViewPage';
import useInterventionActions from './utils/useInterventionActions';
import useModalManagement from './utils/useModalManagement';
import { InterventionViewMode } from './enums';

type Props = {
  viewMode: InterventionViewMode;
};

export default function InterventionContainer({ viewMode }: Props) {
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const initialDepartmentLinksRef = useRef<Option[]>([]);

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.InterventionsManagement) ===
    AccessLevelEnum.Read;
  const isCategoriesViewOnly =
    getUserAccessByComponent(
      AccessComponentEnum.InterventionCategoriesManagement,
    ) === AccessLevelEnum.Read;
  const tasksAccess = getUserAccessByComponent(
    AccessComponentEnum.TasksManagement,
  );
  // Reading an intervention's linked task needs TASKS_MANAGEMENT read, but
  // linking checks on create needs write (the edit form only displays them).
  const canReadTasks =
    tasksAccess === AccessLevelEnum.Read ||
    tasksAccess === AccessLevelEnum.Write;
  const isTasksAvailable =
    viewMode === InterventionViewMode.Add
      ? tasksAccess === AccessLevelEnum.Write
      : canReadTasks;
  const useModalManagementResult = useModalManagement();
  const taskAndCompletionsPicker = useTaskAndCompletionsPicker(formRef);
  const taskFilterLoadOptions = useTasksTableMultiselectFilterLoadOptions();
  const useInterventionActionsResult = useInterventionActions({
    formRef,
    initialDepartmentLinksRef,
    viewMode,
    useModalManagementResult,
    withTask: canReadTasks,
  });
  const {
    breadCrumbsData,
    interventionQueryHookResult,
    interventionDestroyLoading,
    interventionCloseLoading,
    interventionActionDestroyLoading,
    interventionActionCloseLoading,
    onConfirmedInterventionDestroy,
    onConfirmedInterventionClose,
    onConfirmedInterventionActionDestroy,
    onConfirmedInterventionActionClose,
    onViewChange,
    categoryCreateMutationError,
    categoryCreateMutationLoading,
    onAssetCategoryCreate,
  } = useInterventionActionsResult;
  const {
    closeAllModals,
    closeAssetsModalTableMultiselect,
    closeDepartmentsModalTableMultiselect,
    isAssetsModalTableMultiselectOpened,
    isAttachmentPreviewModalOpened,
    isDepartmentsModalTableMultiselectOpened,
    isInterventionActionAssigneesModalOpened,
    isInterventionAssigneesModalOpened,
    isInterventionCloseModalOpened,
    isInterventionDestroyModalOpened,
    isInterventionActionDestroyModalOpened,
    isInterventionActionCloseModalOpened,
    isCategoryFormModalOpened,
  } = useModalManagementResult;

  const interventionId = useInterventionActionsResult.id;

  const onDepartmentsSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        FieldNames.DepartmentLinks,
        values.length
          ? values.map((departmentId) => ({ value: departmentId }))
          : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const onAssetSelect = useCallback(
    (
      values: string[],
      _visibilityMap?: Record<string, boolean>,
      selectedItems?: Asset[],
    ) => {
      if (!formRef.current) return;
      const asset = selectedItems?.[0];
      formRef.current.setValue(
        FieldNames.Asset,
        values[0] && asset
          ? { value: values[0], label: asset.name, data: asset }
          : null,
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    },
    [],
  );

  // Related equipment is scoped to the Site already chosen on the form.
  const siteId = taskAndCompletionsPicker.site?.value;
  const siteLabel = taskAndCompletionsPicker.site?.label;
  const assetFilterOverrides = useMemo(
    () =>
      siteId
        ? {
            [AssetFilterFieldNames.Sites]: [
              { value: siteId, label: siteLabel },
            ],
          }
        : undefined,
    [siteId, siteLabel],
  );
  // ...and to its Site Area, which the asset modal has no filter field for.
  const siteAreaId = taskAndCompletionsPicker.siteArea?.value;
  const assetBaseFilters = useMemo(
    () =>
      siteAreaId
        ? [
            {
              siteAreaId: {
                grouping: FilterGroupingEnum.And,
                predicate: { eq: String(siteAreaId) },
              },
            },
          ]
        : undefined,
    [siteAreaId],
  );
  const assetDisabledFilterFields = useMemo(
    () => [AssetFilterFieldNames.Sectors, AssetFilterFieldNames.Sites],
    [],
  );

  const isForm =
    viewMode === InterventionViewMode.Add ||
    viewMode === InterventionViewMode.Edit;
  const isView =
    viewMode === InterventionViewMode.View ||
    viewMode === InterventionViewMode.ViewActions;
  const canUpdate = interventionQueryHookResult?.data?.canUpdate;

  useEffect(() => {
    if (
      viewMode === InterventionViewMode.Edit &&
      !!canUpdate &&
      !canUpdate.value
    ) {
      onViewChange(InterventionViewMode.View);
    }
  }, [canUpdate, onViewChange, viewMode]);

  if (interventionQueryHookResult.error) {
    return (
      <ErrorPage
        status={getStatusFromError(interventionQueryHookResult.error)}
        description={getDescriptionFromError(interventionQueryHookResult.error)}
      />
    );
  }

  if (interventionQueryHookResult.firstLoading) {
    return <CircleLoader />;
  }

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      {isForm && (
        <InterventionFormPage
          formRef={formRef}
          viewMode={viewMode}
          useInterventionActionsResult={useInterventionActionsResult}
          useModalManagementResult={useModalManagementResult}
          isCategoriesViewOnly={isCategoriesViewOnly}
          taskAndCompletionsPicker={taskAndCompletionsPicker}
          isTasksAvailable={isTasksAvailable}
        />
      )}
      {isView && (
        <InterventionViewPage
          viewMode={viewMode}
          useInterventionActionsResult={useInterventionActionsResult}
          useModalManagementResult={useModalManagementResult}
          isViewOnly={isViewOnly}
        />
      )}
      <Modal
        isOpen={isCategoryFormModalOpened}
        title={t('create-new-intervention-category')}
        onClose={closeAllModals}
      >
        <CategoryForm
          graphQLErrors={getGraphQLErrors(categoryCreateMutationError)}
          loading={categoryCreateMutationLoading}
          onCancel={closeAllModals}
          onSubmit={onAssetCategoryCreate}
        />
      </Modal>
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="intervention-delete-text" />}
        icon={IconName.Delete}
        isOpen={isInterventionDestroyModalOpened}
        loading={interventionDestroyLoading}
        onClose={closeAllModals}
        onConfirm={onConfirmedInterventionDestroy}
        title={t('are-you-sure')}
        useProgress
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="intervention-action-delete-text" />}
        icon={IconName.Delete}
        isOpen={!!isInterventionActionDestroyModalOpened?.id}
        loading={interventionActionDestroyLoading}
        onClose={closeAllModals}
        onConfirm={onConfirmedInterventionActionDestroy}
        title={t('are-you-sure')}
        useProgress
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-close')}
        description={<Trans i18nKey="intervention-close-text" />}
        icon={IconName.Warning}
        isOpen={isInterventionCloseModalOpened}
        loading={interventionCloseLoading}
        onClose={closeAllModals}
        onConfirm={onConfirmedInterventionClose}
        title={t('are-you-sure')}
        useProgress
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-close')}
        description={<Trans i18nKey="intervention-action-close-text" />}
        icon={IconName.Warning}
        isOpen={!!isInterventionActionCloseModalOpened?.id}
        loading={interventionActionCloseLoading}
        onClose={closeAllModals}
        onConfirm={onConfirmedInterventionActionClose}
        title={t('are-you-sure')}
        useProgress
      />
      <FileViewerModal
        attachment={isAttachmentPreviewModalOpened}
        attachmentName={isAttachmentPreviewModalOpened?.filename || ''}
        onClose={closeAllModals}
        isOpen={!!isAttachmentPreviewModalOpened?.id}
      />
      <InterventionAssigneesModal
        caption={t('all-assignees')}
        interventionId={
          isInterventionActionAssigneesModalOpened?.id
            ? undefined
            : useInterventionActionsResult.id
        }
        interventionActionId={isInterventionActionAssigneesModalOpened?.id}
        isOpen={
          isInterventionAssigneesModalOpened ||
          !!isInterventionActionAssigneesModalOpened?.id
        }
        onClose={closeAllModals}
      />
      <DepartmentsModalTableMultiselect
        isOpen={isDepartmentsModalTableMultiselectOpened}
        onClose={closeDepartmentsModalTableMultiselect}
        onSelect={onDepartmentsSelect}
        formRef={formRef}
        fieldName={FieldNames.DepartmentLinks}
        selectedTabFilters={
          viewMode === InterventionViewMode.Edit && interventionId
            ? [
                {
                  departmentInterventionsInterventionId: {
                    grouping: FilterGroupingEnum.And,
                    predicate: { eq: interventionId },
                  },
                },
              ]
            : undefined
        }
      />
      <AssetsModalTableMultiselect
        isOpen={isAssetsModalTableMultiselectOpened}
        onClose={closeAssetsModalTableMultiselect}
        onSelect={onAssetSelect}
        formRef={formRef}
        fieldName={FieldNames.Asset}
        radioSelect
        selectionLimit={1}
        filterOverrides={assetFilterOverrides}
        disabledFilterFields={assetDisabledFilterFields}
        baseFilters={assetBaseFilters}
      />
      {isTasksAvailable && (
        <TaskAndCompletionsModal
          {...taskAndCompletionsPicker.modalProps}
          taskFilterLoadOptions={taskFilterLoadOptions}
          completionUrls={taskCompletionsModalUrls}
        />
      )}
    </>
  );
}
